import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { MoreHorizontal, UserMinus, Shield, Edit3, Eye, Crown } from 'lucide-react';
import { RoleBadge } from './RoleSelector';
import { formatDate } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';

const ROLE_HIERARCHY = { owner: 4, admin: 3, editor: 2, viewer: 1 };

function canManageMember(currentUserRole, targetRole) {
  // Owner can manage anyone
  if (currentUserRole === 'owner') return true;
  // Admin can manage editors and viewers
  if (currentUserRole === 'admin' && ROLE_HIERARCHY[targetRole] < ROLE_HIERARCHY.admin) {
    return true;
  }
  return false;
}

function canChangeToRole(currentUserRole, newRole) {
  // Owners can assign any role
  if (currentUserRole === 'owner') return true;
  // Admins can assign editor/viewer only
  if (currentUserRole === 'admin' && ROLE_HIERARCHY[newRole] < ROLE_HIERARCHY.admin) {
    return true;
  }
  return false;
}

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export default function MembersList({
  projectId,
  members,
  currentUserId,
  currentUserRole,
  onMembersChange,
  loading = false,
}) {
  const [removingMember, setRemovingMember] = useState(null);
  const [updating, setUpdating] = useState(false);

  const handleRoleChange = async (userId, newRole) => {
    if (!canChangeToRole(currentUserRole, newRole)) {
      toast.error('You do not have permission to assign this role');
      return;
    }

    setUpdating(true);
    try {
      await api.put(`/projects/${projectId}/members/${userId}`, { role: newRole });
      toast.success('Role updated successfully');
      onMembersChange();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update role';
      toast.error(message);
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!removingMember) return;

    setUpdating(true);
    try {
      await api.delete(`/projects/${projectId}/members/${removingMember.user_id}`);
      toast.success(`${removingMember.user_name || 'Member'} removed from project`);
      onMembersChange();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to remove member';
      toast.error(message);
    } finally {
      setUpdating(false);
      setRemovingMember(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!members || members.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No members found
      </div>
    );
  }

  const canManage = currentUserRole === 'owner' || currentUserRole === 'admin';

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Member</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Added</TableHead>
            {canManage && <TableHead className="w-[50px]"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => {
            const isCurrentUser = member.user_id === currentUserId;
            const canEdit = canManageMember(currentUserRole, member.role) && !isCurrentUser;

            return (
              <TableRow key={member.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={member.user_avatar} alt={member.user_name} />
                      <AvatarFallback>{getInitials(member.user_name)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium">
                        {member.user_name || 'Unknown User'}
                        {isCurrentUser && (
                          <span className="text-muted-foreground ml-2">(you)</span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {member.user_email}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <RoleBadge role={member.role} />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDate(member.created_at)}
                </TableCell>
                {canManage && (
                  <TableCell>
                    {canEdit && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" disabled={updating}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {currentUserRole === 'owner' && member.role !== 'owner' && (
                            <DropdownMenuItem onClick={() => handleRoleChange(member.user_id, 'owner')}>
                              <Crown className="h-4 w-4 mr-2 text-amber-500" />
                              Make Owner
                            </DropdownMenuItem>
                          )}
                          {canChangeToRole(currentUserRole, 'admin') && member.role !== 'admin' && (
                            <DropdownMenuItem onClick={() => handleRoleChange(member.user_id, 'admin')}>
                              <Shield className="h-4 w-4 mr-2 text-blue-500" />
                              Make Admin
                            </DropdownMenuItem>
                          )}
                          {canChangeToRole(currentUserRole, 'editor') && member.role !== 'editor' && (
                            <DropdownMenuItem onClick={() => handleRoleChange(member.user_id, 'editor')}>
                              <Edit3 className="h-4 w-4 mr-2 text-green-500" />
                              Make Editor
                            </DropdownMenuItem>
                          )}
                          {canChangeToRole(currentUserRole, 'viewer') && member.role !== 'viewer' && (
                            <DropdownMenuItem onClick={() => handleRoleChange(member.user_id, 'viewer')}>
                              <Eye className="h-4 w-4 mr-2 text-gray-500" />
                              Make Viewer
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setRemovingMember(member)}
                          >
                            <UserMinus className="h-4 w-4 mr-2" />
                            Remove from project
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <AlertDialog open={!!removingMember} onOpenChange={() => setRemovingMember(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove member?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{' '}
              <strong>{removingMember?.user_name || 'this member'}</strong> from the project?
              They will lose access to all project content.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              disabled={updating}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {updating ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
